import { Component, OnInit, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '../data.service';
import { ValidationService } from '../validation.service';

@Component({
  selector: 'app-stamp-duty-que',
  templateUrl: './stamp-duty-que.component.html',
  styleUrls: ['./stamp-duty-que.component.css']
})
export class StampDutyQueComponent implements OnInit {
  MapLoad = true;
  lat: number = 0;
  long: number = 0;
  PropertyValue = '';
  calcData: any;
  PurchaseType = '';
  foreignbuyer = '0';
  purchaseDropdownOpen = false;

  purchaseTypes = [
    { value: 'first_time', label: 'First-Time Buyer',         desc: '0% up to £300k · FTB relief on first home' },
    { value: 'investment', label: 'Investment / 2nd Property', desc: 'Standard rates + 5% additional dwelling surcharge' },
    { value: 'primary',    label: 'Primary Residence',         desc: 'Main home purchase — standard SDLT rates' },
    { value: 'non_resi',   label: 'Non-Residential',           desc: 'Commercial or mixed-use property' },
  ];

  constructor(
    public validation: ValidationService,
    private dataService: DataService,
    private router: Router
  ) {
    this.calcData = JSON.parse(localStorage.getItem('calcData'));
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.lat)) { this.lat = this.calcData.lat; }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.long)) { this.long = this.calcData.long; }
  }

  ngOnInit(): void {
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.PropertyValue)) {
      this.PropertyValue = this.validation.amountWithComma(this.calcData.PropertyValue);
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.PurchaseType)) {
      this.PurchaseType = this.calcData.PurchaseType;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.calcData.ForeignBuyer)) {
      this.foreignbuyer = this.calcData.ForeignBuyer;
    }

    // Pre-fill defaults if not already set
    if (!this.PropertyValue) this.PropertyValue = '300,000';
    if (!this.PurchaseType) this.PurchaseType = 'investment';
    this.calcLive();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    const t = e.target as HTMLElement;
    if (!t.closest('.s3-currency-wrap')) { this.purchaseDropdownOpen = false; }
  }

  get locationName(): string {
    return this.calcData?.City || 'United Kingdom';
  }

  get selectedPurchaseName(): string {
    const p = this.purchaseTypes.find(x => x.value === this.PurchaseType);
    return p ? p.label : 'Select purchase type';
  }

  selectPurchaseType(val: string): void {
    this.PurchaseType = val;
    this.purchaseDropdownOpen = false;
    this.calcLive();
  }

  // ── SDLT engine — April 2025 bands ──────────────────────────
  private calcSdltEngine(price: number, type: string, foreign: boolean): number {
    if (!price || price <= 0) return 0;
    let tax = 0;

    if (type === 'non_resi') {
      const tiers = [
        { lower: 0,       upper: 150000,  rate: 0 },
        { lower: 150000,  upper: 250000,  rate: 2 },
        { lower: 250000,  upper: Infinity,rate: 5 },
      ];
      for (const t of tiers) {
        const taxable = Math.max(0, Math.min(price, t.upper) - t.lower);
        tax += taxable * (t.rate / 100);
        if (price <= t.upper) break;
      }
    } else if (type === 'first_time') {
      if (price <= 500000) {
        // FTB relief: 0% up to £300k, 5% on £300k–£500k
        tax += Math.max(0, Math.min(price, 500000) - 300000) * 0.05;
      } else {
        // FTB > £500k loses relief — standard bands apply
        return this.calcSdltEngine(price, 'primary', foreign);
      }
    } else {
      // investment: standard bands + 5% ADS on full value
      // primary: standard bands only
      if (type === 'investment') { tax += price * 0.05; }
      const tiers = [
        { lower: 0,       upper: 125000,  rate: 0  },
        { lower: 125000,  upper: 250000,  rate: 2  },
        { lower: 250000,  upper: 925000,  rate: 5  },
        { lower: 925000,  upper: 1500000, rate: 10 },
        { lower: 1500000, upper: Infinity,rate: 12 },
      ];
      for (const t of tiers) {
        const taxable = Math.max(0, Math.min(price, t.upper) - t.lower);
        tax += taxable * (t.rate / 100);
        if (price <= t.upper) break;
      }
    }

    // Non-resident surcharge: +2% of full value
    if (foreign) { tax += price * 0.02; }
    return tax;
  }

  get livePrice(): number {
    return parseFloat((this.PropertyValue || '').replace(/,/g, '')) || 0;
  }

  get liveSdlt(): number {
    return Math.round(this.calcSdltEngine(this.livePrice, this.PurchaseType, this.foreignbuyer === '1'));
  }

  get liveRate(): number {
    if (!this.livePrice) return 0;
    return parseFloat(((this.liveSdlt / this.livePrice) * 100).toFixed(2));
  }

  get liveForeignSurcharge(): number {
    if (this.foreignbuyer !== '1' || !this.livePrice) return 0;
    return Math.round(this.livePrice * 0.02);
  }

  get liveAdsSurcharge(): number {
    if (this.PurchaseType !== 'investment' || !this.livePrice) return 0;
    return Math.round(this.livePrice * 0.05);
  }

  calcLive(): void { /* getters update automatically */ }

  next(): void {
    let flag = true;
    if (!this.dataService.EmptyNullOrUndefined(this.PropertyValue)) {
      this.calcData.PropertyValue = this.PropertyValue.replace(/,/g, '');
    } else {
      const element = document.getElementById('PropertyValue');
      if (element) { element.classList.add('error-input'); }
      flag = false;
    }
    if (!this.dataService.EmptyNullOrUndefined(this.PurchaseType)) {
      this.calcData.PurchaseType = this.PurchaseType;
    } else {
      flag = false;
    }
    this.calcData.ForeignBuyer = this.foreignbuyer;

    if (flag) {
      this.calcData.reportSavedOnServer = false;
      localStorage.setItem('calcData', JSON.stringify(this.calcData));
      this.router.navigate(['/stamp-duty-calculator/result']);
    }
  }
}
